import React, { useState, useCallback, useEffect } from "react";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import {
  Button,
  Typography,
  message,
  Popconfirm,
  Alert,
  Modal,
  Input,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getDictionaryList,
  deleteDictionary,
  exportDictionary,
} from "./api/DictionaryAPI";
import {
  IDictionary,
  IAntDProTableRequest,
} from "./type/DictionaryManagementType";
import { FaPlus, FaEdit, FaTrash, FaBook, FaFileExport } from "react-icons/fa";
import { debounce } from "lodash";

const { Title } = Typography;

const DictionaryManagement: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // parse URLSearchParams to queryParams
  const parseSearchParams = (search: string): IAntDProTableRequest => {
    const params = new URLSearchParams(search);
    return {
      params: {
        current: parseInt(params.get("page") || "1"),
        pageSize: 100,
      },
      filter: {
        name: params.get("name") || undefined,
      },
    };
  };

  // convert queryParams to URLSearchParams string
  const toSearchParams = (queryParams: IAntDProTableRequest): string => {
    const params = new URLSearchParams();

    if (queryParams.params.current) {
      params.set("page", queryParams.params.current.toString());
    }

    if (queryParams.filter) {
      Object.entries(queryParams.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, value.toString());
        }
      });
    }

    return params.toString();
  };

  const [queryParams, setQueryParams] = useState<IAntDProTableRequest>(
    parseSearchParams(location.search)
  );

  useEffect(() => {
    const searchString = toSearchParams(queryParams);

    // remove '?'
    const currentSearch = location.search.substring(1);

    if (searchString !== currentSearch) {
      navigate(`${location.pathname}?${searchString}`, { replace: true });
    }
  }, [queryParams, navigate, location.pathname]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["dictionary", queryParams],
    queryFn: () => getDictionaryList(queryParams),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDictionary,
    onSuccess: () => {
      message.success("Dictionary deleted successfully.");
      queryClient.invalidateQueries({
        queryKey: ["dictionary"],
        refetchType: "all",
      });
    },
    onError: (error) => {
      console.error("Error deleting dictionary:", error);
      message.error("Failed to delete dictionary.");
    },
  });

  const [exportingDictionaryId, setExportingDictionaryId] = useState<
    string | null
  >(null);

  const exportMutation = useMutation({
    mutationFn: exportDictionary,
    onSuccess: ({ data, filename }, id) => {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Show the success message after initiating the download
      setTimeout(() => {
        message.success("Dictionary exported successfully.");
      }, 100);
      setExportingDictionaryId(null);
    },
    onError: (error) => {
      console.error("Error exporting dictionary:", error);
      message.error("Failed to export dictionary.");
      setExportingDictionaryId(null);
    },
  });

  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pin, setPin] = useState("");
  const [dictionaryToDelete, setDictionaryToDelete] = useState<string | null>(
    null
  );

  const handleDelete = (id: string) => {
    setDictionaryToDelete(id);
    setPinModalVisible(true);
  };

  const confirmDelete = () => {
    if (pin === "SpeakHokkien" && dictionaryToDelete) {
      deleteMutation.mutate(dictionaryToDelete);
      setPinModalVisible(false);
      setPin("");
      setDictionaryToDelete(null);
    } else {
      message.error("Incorrect PIN. Deletion cancelled.");
    }
  };

  const handleExport = (id: string) => {
    setExportingDictionaryId(id);
    exportMutation.mutate(id);
  };

  const debouncedSetFilters = useCallback(
    debounce((newParams: Partial<IAntDProTableRequest>) => {
      setQueryParams((prev) => ({
        ...prev,
        ...newParams,
        params: { ...prev.params, current: 1 }, // Reset to first page on filter change
      }));
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
    };
  }, [debouncedSetFilters]);

  const columns: ProColumns<IDictionary>[] = [
    {
      title: "Source title",
      dataIndex: "name",
      render: (text, record) => (
        <a
          onClick={() =>
            record.id &&
            navigate(`/vocab?dictionaryId=${encodeURIComponent(record.id)}`)
          }
        >
          {text}
        </a>
      ),
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetFilters({
            filter: { ...queryParams.filter, name: e.target.value },
          }),
      },
    },
    {
      title: "Number of entries",
      dataIndex: "totalVocab",
      search: false,
    },
    {
      title: "Author",
      dataIndex: "author",
      search: false,
    },
    {
      title: "Remark",
      dataIndex: "remark",
      search: false,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      search: false,
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            icon={<FaEdit />}
            onClick={() => navigate(`/dictionary/${record.id}`)}
            title="Edit"
          />
          <Button
            icon={<FaTrash />}
            onClick={() => record.id && handleDelete(record.id)}
            title="Delete"
          />
          <Button
            onClick={() => record.id && handleExport(record.id)}
            loading={exportingDictionaryId === record.id}
          >
            Export
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="Error loading dictionaries"
        description={(error as Error).message}
        type="error"
      />
    );
  }

  return (
    <div style={{ paddingInline: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Manage dictionaries</Title>
        <Button
          type="primary"
          onClick={() => navigate("/dictionary/new")}
          icon={<FaPlus />}
        >
          Create
        </Button>
      </div>
      <ProTable<IDictionary>
        columns={columns}
        dataSource={data?.results}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          total: data?.totalResults || 0,
          pageSize: 100,
          current: queryParams.params.current || 1,
          onChange: (page) => {
            setQueryParams((prev) => ({
              ...prev,
              params: {
                ...prev.params,
                current: page || 1,
                pageSize: 100,
              },
            }));
          },
          showSizeChanger: false,
        }}
        search={{
          labelWidth: "auto",
        }}
        onSubmit={(params) => {
          setQueryParams((prev) => ({
            ...prev,
            params: {
              current: 1,
              pageSize: 100,
            },
            filter: {
              name: params.name,
            },
          }));
        }}
        onReset={() => {
          setQueryParams({
            params: { current: 1, pageSize: 100 },
            filter: {},
          });
        }}
      />
      <Modal
        title="Enter PIN to delete"
        open={pinModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setPinModalVisible(false);
          setPin("");
          setDictionaryToDelete(null);
        }}
      >
        <Input
          type="password"
          placeholder="Enter PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default DictionaryManagement;
